import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCheckoutAddresses } from "../hooks/use-checkout-addresses";
import { useCheckoutStore } from "../store/checkout-store";
import type { AddressFormStatus } from "../store/checkout-store";
import { addressFormBridge } from "../utils/address-form-bridge";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import type { Address } from "@/api/models/api-types";
import {
  useCountries,
  useCities,
  useTowns,
  useQuarters,
} from "@/api/hooks/use-query-api";
import { LoadingSpinner } from "@/components/common/loading-spinner";

// Address form schema
const addressSchema = z.object({
  label: z.string().min(1, "Address title is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  cityId: z.number().optional(),
  state: z.string().min(1, "State is required"),
  townId: z.number().optional(),
  quarter: z.string().optional(),
  quarterId: z.string().optional(),
  postalCode: z.string().min(1, "Postal code is required"),
  country: z.string().min(1, "Country is required"),
  countryId: z.number().optional(),
  phone: z.string().optional(),
  isDefault: z.boolean().optional(),
});

type AddressFormData = z.infer<typeof addressSchema>;

interface AddressFormInlineProps {
  editingAddress?: Address | null;
  onCancel: () => void;
  mode?: "delivery" | "billing";
}

const getAddressFormStatus = (values: AddressFormData): AddressFormStatus => {
  const parsed = addressSchema.safeParse(values);
  if (parsed.success) return "valid";

  const hasAnyValue = Boolean(
    values.label ||
      values.firstName ||
      values.lastName ||
      values.addressLine1 ||
      values.city ||
      values.state ||
      values.postalCode ||
      values.country ||
      values.phone
  );

  return hasAnyValue ? "invalid" : "empty";
};

const isTurkeyCountry = (country?: string) => {
  const normalized = (country || "")
    .toLocaleUpperCase("tr-TR")
    .replace(/İ/g, "I");
  return (
    normalized.includes("TURK") ||
    normalized.includes("TURKIYE") ||
    normalized === "TR"
  );
};

export const AddressFormInline: React.FC<AddressFormInlineProps> = ({
  editingAddress,
  onCancel,
  mode = "delivery",
}) => {
  const { t } = useTranslation();
  const { saveAddress, isSaving } = useCheckoutAddresses();
  const setDeliveryAddressFormStatus = useCheckoutStore(
    (state) => state.setDeliveryAddressFormStatus
  );
  const setBillingAddressFormStatus = useCheckoutStore(
    (state) => state.setBillingAddressFormStatus
  );
  const addressFormFocusToken = useCheckoutStore(
    (state) => state.addressFormFocusToken
  );
  const formRef = useRef<HTMLFormElement>(null);
  const [showApartmentField, setShowApartmentField] = useState(
    Boolean(editingAddress?.addressLine2)
  );

  // Location API Hooks
  const { data: countriesData, isLoading: isLoadingCountries } = useCountries();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: editingAddress || {
      label: "",
      firstName: "",
      lastName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      cityId: undefined,
      state: "",
      townId: undefined,
      quarter: "",
      quarterId: undefined,
      postalCode: "",
      country: "",
      countryId: undefined,
      phone: "",
      isDefault: false,
    },
  });

  // Watch form values for cascade selection
  const selectedCountryId = watch("countryId");
  const selectedCityId = watch("cityId");
  const selectedTownId = watch("townId");
  const selectedQuarterId = watch("quarterId");

  // Fetch cities when country is selected
  const { data: citiesData, isLoading: isLoadingCities } = useCities(
    selectedCountryId || null
  );

  // Fetch towns when city is selected
  const { data: townsData, isLoading: isLoadingTowns } = useTowns(
    selectedCountryId || null,
    selectedCityId || null
  );

  // Fetch quarters when town is selected
  const { data: quartersData, isLoading: isLoadingQuarters } = useQuarters(
    selectedCountryId || null,
    selectedCityId || null,
    selectedTownId || null
  );

  // Prepare countries list
  const countries = useMemo(() => {
    if (!countriesData?.data) return [];
    return countriesData.data.map((country) => ({
      value: country.countryId.toString(),
      label: country.name,
      countryId: country.countryId,
    }));
  }, [countriesData]);

  // Prepare cities list
  const cities = useMemo(() => {
    if (!citiesData?.data) return [];
    return citiesData.data.map((city) => ({
      value: city.cityId.toString(),
      label: city.name,
      cityId: city.cityId,
    }));
  }, [citiesData]);

  // Prepare towns list
  const towns = useMemo(() => {
    if (!townsData?.data) return [];
    return townsData.data.map((town) => ({
      value: town.townId.toString(),
      label: town.name,
      townId: town.townId,
    }));
  }, [townsData]);

  // Prepare quarters list
  const quarters = useMemo(() => {
    if (!quartersData?.data) return [];
    return quartersData.data.map((quarter) => ({
      value: quarter._id,
      label: quarter.name,
      quarterId: quarter._id,
    }));
  }, [quartersData]);

  // Handle country change - reset dependent fields
  const handleCountryChange = (value: string) => {
    const country = countries.find((c) => c.value === value);
    if (country) {
      setValue("countryId", country.countryId);
      setValue("country", country.label);
      // Reset dependent fields
      setValue("city", "");
      setValue("cityId", undefined);
      setValue("state", "");
      setValue("townId", undefined);
      setValue("quarter", "");
      setValue("quarterId", undefined);
    }
  };

  // Handle city change - reset dependent fields
  const handleCityChange = (value: string) => {
    const city = cities.find((c) => c.value === value);
    if (city) {
      setValue("cityId", city.cityId);
      setValue("city", city.label);
      // Reset dependent fields
      setValue("state", "");
      setValue("townId", undefined);
      setValue("quarter", "");
      setValue("quarterId", undefined);
    }
  };

  // Handle town change - reset dependent fields
  const handleTownChange = (value: string) => {
    const town = towns.find((t) => t.value === value);
    if (town) {
      setValue("townId", town.townId);
      setValue("state", town.label);
      // Reset dependent fields
      setValue("quarter", "");
      setValue("quarterId", undefined);
    }
  };

  // Handle quarter change
  const handleQuarterChange = (value: string) => {
    const quarter = quarters.find((q) => q.value === value);
    if (quarter) {
      setValue("quarterId", quarter.quarterId);
      setValue("quarter", quarter.label);
    }
  };

  // Check if cities are available for selected country
  const hasCities = cities.length > 0;
  const hasTowns = towns.length > 0;
  const hasQuarters = quarters.length > 0;

  // Set countryId when countries data is loaded and editingAddress has country
  useEffect(() => {
    if (editingAddress && countries.length > 0 && !selectedCountryId) {
      const country = countries.find(
        (c) => c.label === editingAddress.country || c.label.toUpperCase() === editingAddress.country?.toUpperCase()
      );
      if (country) {
        setValue("countryId", country.countryId);
        setValue("country", country.label);
      }
    }
  }, [countries, editingAddress, selectedCountryId, setValue]);

  // Set cityId when cities data is loaded and editingAddress has city
  useEffect(() => {
    if (editingAddress && cities.length > 0 && selectedCountryId && !selectedCityId) {
      const city = cities.find(
        (c) => c.label === editingAddress.city || c.label.toUpperCase() === editingAddress.city?.toUpperCase()
      );
      if (city) {
        setValue("cityId", city.cityId);
        setValue("city", city.label);
      }
    }
  }, [cities, editingAddress, selectedCountryId, selectedCityId, setValue]);

  // Set townId when towns data is loaded and editingAddress has state
  useEffect(() => {
    if (editingAddress && towns.length > 0 && selectedCityId && !selectedTownId) {
      const town = towns.find(
        (t) => t.label === editingAddress.state || t.label.toUpperCase() === editingAddress.state?.toUpperCase()
      );
      if (town) {
        setValue("townId", town.townId);
        setValue("state", town.label);
      }
    }
  }, [towns, editingAddress, selectedCityId, selectedTownId, setValue]);

  // Set quarterId when quarters data is loaded and editingAddress has quarter
  useEffect(() => {
    if (editingAddress && quarters.length > 0 && selectedTownId && !selectedQuarterId) {
      const quarter = quarters.find(
        (q) => q.label === (editingAddress as any).quarter || q.label.toUpperCase() === (editingAddress as any).quarter?.toUpperCase()
      );
      if (quarter) {
        setValue("quarterId", quarter.quarterId);
        setValue("quarter", quarter.label);
      }
    }
  }, [quarters, editingAddress, selectedTownId, selectedQuarterId, setValue]);

  const watchedValues = watch();

  useEffect(() => {
    const status = getAddressFormStatus(watchedValues);
    if (mode === "billing") {
      setBillingAddressFormStatus(status);
    } else {
      setDeliveryAddressFormStatus(status);
    }
  }, [
    watchedValues,
    mode,
    setBillingAddressFormStatus,
    setDeliveryAddressFormStatus,
  ]);

  useEffect(() => {
    return () => {
      if (mode === "billing") {
        setBillingAddressFormStatus("closed");
      } else {
        setDeliveryAddressFormStatus("closed");
      }
    };
  }, [mode, setBillingAddressFormStatus, setDeliveryAddressFormStatus]);

  const persistAddress = async (data: AddressFormData) => {
    await saveAddress(data, editingAddress?._id, mode);
    onCancel();
  };

  const persistAddressRef = useRef(persistAddress);
  persistAddressRef.current = persistAddress;

  useEffect(() => {
    const submitPending = () =>
      new Promise<boolean>((resolve) => {
        void handleSubmit(
          async (data) => {
            try {
              await persistAddressRef.current(data);
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          () => resolve(false)
        )();
      });

    if (mode === "billing") {
      addressFormBridge.registerBilling(submitPending);
    } else {
      addressFormBridge.registerDelivery(submitPending);
    }

    return () => {
      if (mode === "billing") {
        addressFormBridge.registerBilling(null);
      } else {
        addressFormBridge.registerDelivery(null);
      }
    };
  }, [handleSubmit, mode]);

  useEffect(() => {
    if (addressFormFocusToken > 0) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [addressFormFocusToken]);

  const onSubmit = async (data: AddressFormData) => {
    await persistAddress(data);
  };

  const phonePlaceholder = isTurkeyCountry(watchedValues.country)
    ? t("checkout.phonePlaceholderTR")
    : t("checkout.phonePlaceholderIntl");

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3 md:space-y-4 pt-3 md:pt-4"
        >
          {!editingAddress && (
            <div className="rounded-[10px] border border-primary/20 bg-primary/5 dark:bg-primary/10 px-3 py-2.5">
              <p className="text-xs md:text-sm text-text-secondary dark:text-gray-300">
                {t("checkout.addressNotSavedYet")}
              </p>
            </div>
          )}
          {/* First Name and Last Name */}
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="space-y-1.5 md:space-y-2">
              <Label
                htmlFor="firstName"
                className="text-xs md:text-sm font-medium"
              >
                {t("checkout.firstName")}
              </Label>
              <Input
                id="firstName"
                placeholder={t("checkout.firstNamePlaceholder")}
                {...register("firstName")}
                className={errors.firstName ? "border-destructive" : ""}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5 md:space-y-2">
              <Label
                htmlFor="lastName"
                className="text-xs md:text-sm font-medium"
              >
                {t("checkout.lastName")}
              </Label>
              <Input
                id="lastName"
                placeholder={t("checkout.lastNamePlaceholder")}
                {...register("lastName")}
                className={errors.lastName ? "border-destructive" : ""}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          {/* Address Title */}
          <div className="space-y-1.5 md:space-y-2">
            <Label htmlFor="label" className="text-xs md:text-sm font-medium">
              {t("checkout.addressTitle")}
            </Label>
            <Input
              id="label"
              placeholder={t("checkout.addressTitlePlaceholder")}
              {...register("label")}
              className={errors.label ? "border-destructive" : ""}
            />
            {errors.label && (
              <p className="text-sm text-destructive">{errors.label.message}</p>
            )}
          </div>

          {/* Country, City, State, Quarter, ZIP */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {/* Country */}
            <div className="space-y-1.5 md:space-y-2">
              <Label
                htmlFor="country"
                className="text-xs md:text-sm font-medium"
              >
                {t("checkout.country")}
              </Label>
              {isLoadingCountries ? (
                <div className="flex items-center justify-center py-2">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <Select
                  value={selectedCountryId?.toString() || ""}
                  onValueChange={handleCountryChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("checkout.selectCountry")} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {errors.country && (
                <p className="text-sm text-destructive">
                  {errors.country.message}
                </p>
              )}
            </div>

            {/* City */}
            {selectedCountryId && (
              <div className="space-y-1.5 md:space-y-2">
                <Label
                  htmlFor="city"
                  className="text-xs md:text-sm font-medium"
                >
                  {t("checkout.city")}
                </Label>
                {isLoadingCities ? (
                  <div className="flex items-center justify-center py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : hasCities ? (
                  <Select
                    value={selectedCityId?.toString() || ""}
                    onValueChange={handleCityChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("checkout.selectCity")} />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((city) => (
                        <SelectItem key={city.value} value={city.value}>
                          {city.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="city"
                    placeholder={t("checkout.cityPlaceholder")}
                    {...register("city")}
                    className={errors.city ? "border-destructive" : ""}
                  />
                )}
                {errors.city && (
                  <p className="text-sm text-destructive">
                    {errors.city.message}
                  </p>
                )}
              </div>
            )}

            {/* Town/State */}
            {selectedCityId && (
              <div className="space-y-1.5 md:space-y-2">
                <Label
                  htmlFor="state"
                  className="text-xs md:text-sm font-medium"
                >
                  {t("checkout.state")}
                </Label>
                {isLoadingTowns ? (
                  <div className="flex items-center justify-center py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : hasTowns ? (
                  <Select
                    value={selectedTownId?.toString() || ""}
                    onValueChange={handleTownChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("checkout.selectState")} />
                    </SelectTrigger>
                    <SelectContent>
                      {towns.map((town) => (
                        <SelectItem key={town.value} value={town.value}>
                          {town.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="state"
                    placeholder={t("checkout.statePlaceholder")}
                    {...register("state")}
                    className={errors.state ? "border-destructive" : ""}
                  />
                )}
                {errors.state && (
                  <p className="text-sm text-destructive">
                    {errors.state.message}
                  </p>
                )}
              </div>
            )}

            {/* Quarter (Optional) */}
            {selectedTownId && hasQuarters && (
              <div className="space-y-1.5 md:space-y-2">
                <Label
                  htmlFor="quarter"
                  className="text-xs md:text-sm font-medium"
                >
                  {t("checkout.quarter")}{" "}
                  <span className="text-muted-foreground">
                    ({t("common.optional")})
                  </span>
                </Label>
                {isLoadingQuarters ? (
                  <div className="flex items-center justify-center py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : (
                  <Select
                    value={selectedQuarterId || ""}
                    onValueChange={handleQuarterChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("checkout.selectQuarter")} />
                    </SelectTrigger>
                    <SelectContent>
                      {quarters.map((quarter) => (
                        <SelectItem key={quarter.value} value={quarter.value}>
                          {quarter.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* ZIP Code */}
            <div className="space-y-1.5 md:space-y-2">
              <Label
                htmlFor="postalCode"
                className="text-xs md:text-sm font-medium"
              >
                {t("checkout.zipCode")}
              </Label>
              <Input
                id="postalCode"
                placeholder="34000"
                {...register("postalCode")}
                className={errors.postalCode ? "border-destructive" : ""}
              />
              {errors.postalCode && (
                <p className="text-sm text-destructive">
                  {errors.postalCode.message}
                </p>
              )}
            </div>
          </div>

          {/* Address Line 1 */}
          <div className="space-y-1.5 md:space-y-2">
            <Label
              htmlFor="addressLine1"
              className="text-xs md:text-sm font-medium"
            >
              {t("checkout.address")}
            </Label>
            <Input
              id="addressLine1"
              placeholder={t("checkout.addressPlaceholder")}
              {...register("addressLine1")}
              className={errors.addressLine1 ? "border-destructive" : ""}
            />
            {errors.addressLine1 && (
              <p className="text-sm text-destructive">
                {errors.addressLine1.message}
              </p>
            )}
          </div>

          {/* Address Line 2 (Optional) */}
          <div className="space-y-1.5 md:space-y-2">
            {!showApartmentField ? (
              <button
                type="button"
                className="text-xs md:text-sm text-primary hover:underline"
                onClick={() => setShowApartmentField(true)}
              >
                + {t("checkout.addApartmentSuite")}
              </button>
            ) : (
              <Input
                id="addressLine2"
                placeholder={t("checkout.apartmentSuite")}
                autoFocus={!editingAddress?.addressLine2}
                {...register("addressLine2")}
              />
            )}
          </div>

          {/* Phone (Optional) */}
          <div className="space-y-1.5 md:space-y-2">
            <Label htmlFor="phone" className="text-xs md:text-sm font-medium">
              {t("checkout.phone")}{" "}
              <span className="text-muted-foreground">
                ({t("common.optional")})
              </span>
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder={phonePlaceholder}
              {...register("phone")}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-3 md:pt-4 border-t border-border-secondary dark:border-gray-700">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 text-xs md:text-sm py-2 md:py-2.5"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving}
              className="flex-[1.6] text-xs md:text-sm py-2 md:py-2.5"
            >
              {isSaving
                ? t("common.saving")
                : editingAddress
                ? t("common.update")
                : t("checkout.useThisAddress")}
            </Button>
          </div>
        </form>
      </motion.div>
    </AnimatePresence>
  );
};
